import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useCallback } from 'react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { MobileNavProvider, useMobileNav } from './contexts/MobileNavContext'
import { SubscriptionProvider } from './contexts/SubscriptionContext'
import ProtectedRoute from './components/Auth/ProtectedRoute'
import Sidebar from './components/Layout/Sidebar'
import Header from './components/Layout/Header'
import Inbox from './pages/Inbox'
import Sent from './pages/Sent'
import Login from './pages/Login'
import VerifyEmail from './pages/VerifyEmail'
import LandingPage from './pages/LandingPage'
import ComposeModal from './components/Email/ComposeModal'
import { fetchAllEmailsFromAccounts } from './services/emailService'

// Component that requires email verification
function VerifiedRoute({ children }: { children: React.ReactNode }) {
  const { isEmailVerified } = useAuth()

  if (!isEmailVerified) {
    return <VerifyEmail />
  }

  return <>{children}</>
}

function AppLayout() {
  const { user } = useAuth()
  const { isSidebarOpen, openSidebar, closeSidebar } = useMobileNav()
  const [isComposeOpen, setIsComposeOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const handleRefresh = useCallback(async () => {
    if (!user || isRefreshing) return

    setIsRefreshing(true)
    try {
      // Fetch from both INBOX and SENT folders
      // Use higher limit to support 30-day retention (~500 emails)
      const [inboxResult, sentResult] = await Promise.all([
        fetchAllEmailsFromAccounts('INBOX', 500),
        fetchAllEmailsFromAccounts('SENT', 200)
      ])

      console.log('Inbox fetch result:', inboxResult)
      console.log('Sent fetch result:', sentResult)

      // Trigger re-render in child components
      setRefreshTrigger(prev => prev + 1)
    } catch (error) {
      console.error('Failed to refresh emails:', error)
    } finally {
      setIsRefreshing(false)
    }
  }, [user, isRefreshing])

  const handleCompose = useCallback(() => {
    closeSidebar()
    setIsComposeOpen(true)
  }, [closeSidebar])

  return (
    <div className="h-full flex bg-gray-50">
      <Sidebar
        onCompose={handleCompose}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          onMenuClick={openSidebar}
        />

        <main className="flex-1 overflow-hidden">
          <Routes>
            <Route path="/inbox" element={<Inbox searchQuery={searchQuery} refreshTrigger={refreshTrigger} />} />
            <Route path="/sent" element={<Sent searchQuery={searchQuery} refreshTrigger={refreshTrigger} />} />
            <Route path="*" element={<Navigate to="/inbox" replace />} />
          </Routes>
        </main>
      </div>

      {isComposeOpen && (
        <ComposeModal onClose={() => setIsComposeOpen(false)} />
      )}
    </div>
  )
}

// Landing page wrapper - shows landing for unauthenticated users
function LandingRoute() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    )
  }

  // If user is logged in, redirect to inbox
  if (user) {
    return <Navigate to="/inbox" replace />
  }

  return <LandingPage />
}

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <MobileNavProvider>
          <Routes>
            <Route path="/" element={<LandingRoute />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <VerifiedRoute>
                    <AppLayout />
                  </VerifiedRoute>
                </ProtectedRoute>
              }
            />
          </Routes>
        </MobileNavProvider>
      </SubscriptionProvider>
    </AuthProvider>
  )
}

export default App
